import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import api from "../supabase/axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import Layout from "../layout/Layout";
import { useLoading } from "../context/LoadingContext"; 

const TextInput = ({
  type = "text",
  name,
  placeholder,
  value,
  onChange,
  error,
  autoComplete,
}) => (
  <>
    <input
      type={type}
      name={name}
      autoComplete={autoComplete}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      aria-invalid={!!error}
      aria-describedby={`${name}-error`}
      className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:border-blue-600 transition-colors"
    />
    {error && (
      <p id={`${name}-error`} className="text-sm text-red-500">
        {error}
      </p>
    )}
  </>
);

const AuthForm = () => {
  const [isRegistering, setIsRegistering] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const { setLoading } = useLoading(); 
  const [errors, setErrors] = useState({});
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetData, setResetData] = useState({
    email: "",
    otp: "",
    newPassword: "",
    confirmNewPassword: "",
  });
  const [resetErrors, setResetErrors] = useState({});
  const [resetOtpSent, setResetOtpSent] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
      setLoading(true);
  
      const timer = setTimeout(() => {
        setLoading(false);
      }, 1000);
  
      return () => clearTimeout(timer);
    }, [setLoading]);

  const resetForm = () => {
    setFormData({ fullName: "", email: "", password: "", confirmPassword: "" });
    setErrors({});
    setOtp("");
  };

  const resetPasswordForm = () => {
    setResetData({ email: "", otp: "", newPassword: "", confirmNewPassword: "" });
    setResetErrors({});
    setResetOtpSent(false);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setErrors((prev) => ({ ...prev, [e.target.name]: "" }));
  };

  const handleResetChange = (e) => {
    setResetData({ ...resetData, [e.target.name]: e.target.value });
    setResetErrors((prev) => ({ ...prev, [e.target.name]: "" }));
  };

  const validate = () => {
    const newErrors = {};
    if (isRegistering && !formData.fullName.trim()) {
      newErrors.fullName = "Full name is required";
    }
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }
    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }
    if (isRegistering && formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }
    if (isRegistering && otpSent && !otp.trim()) {
      newErrors.otp = "OTP is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateReset = () => {
    const newErrors = {};
    if (!resetData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(resetData.email)) {
      newErrors.email = "Email is invalid";
    }
    if (resetOtpSent) {
      if (!resetData.otp.trim()) {
        newErrors.otp = "OTP is required";
      }
      if (!resetData.newPassword) {
        newErrors.newPassword = "New password is required";
      } else if (resetData.newPassword.length < 6) {
        newErrors.newPassword = "New password must be at least 6 characters";
      }
      if (resetData.newPassword !== resetData.confirmNewPassword) {
        newErrors.confirmNewPassword = "Passwords do not match";
      }
    }
    setResetErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    setLoading(true);
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);

    try {
      if (isRegistering) {
        if (!otpSent) {
          const res = await api.post(`/api/auth/register/send-otp`, {
            Email: formData.email,
            FullName: formData.fullName,
            Password: formData.password,
          });
          setOtpSent(true);
          toast.dismiss();
          toast.success(res.data.message || "OTP sent to your email.");
        } else {
          const res = await api.post(`/api/auth/register/verify`, {
            Email: formData.email,
            FullName: formData.fullName,
            Password: formData.password,
            otp: otp,
          });
          toast.dismiss();
          toast.success(res.data.message || "Registration successful. You can now login.");
          setIsRegistering(false);
          resetForm();
          setOtpSent(false);
        }
      } else {
        const res = await api.post(`/api/auth/login`, {
          Email: formData.email,
          Password: formData.password,
        });

        const { user } = res.data;
        toast.dismiss();
        toast.success("Login successful!");

        if (user.role === "Admin") {
          navigate("/admin");
        } else {
          navigate("/profile");
        }
      }
    } catch (error) {
      toast.dismiss();
      toast.error(error.response?.data?.error || error.message || "An error occurred.");
    } finally {
      setIsSubmitting(false);
      setLoading(false);
    }
  };

  const handleGoogleSSO = () => {
    setLoading(true);
    if (!process.env.REACT_APP_GOOGLE_CLIENT_ID) {
      toast.error("Google login is not configured. Please contact support.");
      return;
    }

    const redirectUri = `${window.location.origin}/auth/callback`;
    const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${process.env.REACT_APP_GOOGLE_CLIENT_ID}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=id_token&scope=openid%20profile%20email&nonce=${Math.random().toString(36).substring(2)}`;
    window.location.href = authUrl;
    setLoading(false);
  };

  const handleForgotPasswordSubmit = async (e) => {
    setLoading(true);
    e.preventDefault();
    if (!validateReset()) return;

    setIsSubmitting(true);

    try {
      if (!resetOtpSent) {
        const res = await api.post(`/api/auth/reset-password/send-otp`, {
          Email: resetData.email,
        });
        if (res.status === 200) {
          setResetOtpSent(true);
          toast.dismiss();
          toast.success(res.data.message || "OTP sent to your email.");
        }
      } else {
        const res = await api.post(`/api/auth/reset-password/verify`, {
          Email: resetData.email,
          otp: resetData.otp,
          NewPassword: resetData.newPassword,
        });
        toast.dismiss();
        toast.success(res.data.message || "Password reset successful. You can now login.");
        setShowForgotPassword(false);
        resetPasswordForm();
      }
    } catch (error) {
      toast.dismiss();
      const errorMessage = error.response?.data?.error || error.message || "An error occurred.";
      if (errorMessage === "Email not found") {
        setResetErrors((prev) => ({ ...prev, email: "Email not found" }));
      } else if (errorMessage === "Invalid or expired OTP") {
        setResetErrors((prev) => ({ ...prev, otp: "Invalid or expired OTP" }));
      } else if (errorMessage === "New password must be at least 6 characters") {
        setResetErrors((prev) => ({ ...prev, newPassword: "New password must be at least 6 characters" }));
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setIsSubmitting(false);
      setLoading(false);
    }
  };

  const url =
    typeof window !== "undefined"
      ? window.location.origin + "/account"
      : "https://www.shahumumbai.com/account";

  return (
    <Layout>
      <Helmet>
        <title>{isRegistering ? "Create Account — Shahu Mumbai" : "Sign In — Shahu Mumbai"}</title>
        <meta
          name="description"
          content={
            isRegistering
              ? "Create your Shahu Mumbai account to manage orders and wishlist."
              : "Sign in to Shahu Mumbai to access your profile, orders, and wishlist."
          }
        />
        <link rel="canonical" href={url} />
        <meta name="robots" content="noindex,nofollow" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={isRegistering ? "Create Account — Shahu Mumbai" : "Sign In — Shahu Mumbai"} />
        <meta property="og:description" content="Account access for Shahu Mumbai customers." />
        <meta property="og:url" content={url} />
        <meta name="twitter:card" content="summary" />
      </Helmet>

      <div className="flex justify-center items-center py-12 px-4 sm:px-6 lg:px-8 min-h-36">
        <div className="bg-white p-6 sm:p-8 rounded-lg w-full max-w-sm shadow-md">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
              {isRegistering ? "Register" : "Sign In"}
            </h2>
            <button
              onClick={() => {
                setIsRegistering(!isRegistering);
                resetForm();
                setOtpSent(false);
              }}
              className="text-sm text-blue-600 hover:text-blue-700 transition-colors"
            >
              {isRegistering ? "Have an account? Sign In" : "Create Account"}
            </button>
          </div>

          <button
            type="button"
            onClick={handleGoogleSSO}
            disabled={!process.env.REACT_APP_GOOGLE_CLIENT_ID}
            className={`flex items-center justify-center gap-2 bg-white border border-gray-300 py-2 text-sm font-medium rounded-md transition-colors mb-4 w-full ${
              !process.env.REACT_APP_GOOGLE_CLIENT_ID ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-100"
            }`}
          >
            <img src="/google-icon.svg" alt="Google" className="w-5 h-5" />
            {isRegistering ? "Sign up with Google" : "Sign in with Google"}
          </button>

          <div className="relative text-center my-3 text-sm text-gray-500">
            <span className="before:absolute before:left-0 before:top-1/2 before:w-[40%] before:h-px before:bg-gray-300 after:absolute after:right-0 after:top-1/2 after:w-[40%] after:h-px after:bg-gray-300">
              OR
            </span>
          </div>

          <form className="flex flex-col gap-3" onSubmit={handleSubmit}>
            {isRegistering && (
              <TextInput
                name="fullName"
                placeholder="Full Name"
                value={formData.fullName}
                onChange={handleChange}
                error={errors.fullName}
                autoComplete="name"
              />
            )}

            <TextInput
              type="email"
              name="email"
              placeholder="Email"
              value={formData.email}
              onChange={handleChange}
              error={errors.email}
              autoComplete="email"
            />

            <TextInput
              type="password"
              name="password"
              placeholder="Password"
              value={formData.password}
              onChange={handleChange}
              error={errors.password}
              autoComplete={isRegistering ? "new-password" : "current-password"}
            />

            {isRegistering && (
              <TextInput
                type="password"
                name="confirmPassword"
                placeholder="Confirm Password"
                value={formData.confirmPassword}
                onChange={handleChange}
                error={errors.confirmPassword}
                autoComplete="new-password"
              />
            )}

            {isRegistering && otpSent && (
              <TextInput
                name="otp"
                placeholder="Enter OTP"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                error={errors.otp}
              />
            )}

            {!isRegistering && (
              <div className="flex justify-between items-center">
                <label className="text-sm text-gray-700 flex items-center">
                  <input type="checkbox" className="mr-2" />
                  Remember me
                </label>
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="text-sm text-blue-600 hover:text-blue-700 transition-colors"
                >
                  Forgot Password?
                </button>
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className={`bg-gray-900 text-white font-semibold py-2 rounded-md transition-colors ${
                isSubmitting ? "opacity-60 cursor-not-allowed" : "hover:bg-gray-800"
              }`}
            >
              {isSubmitting
                ? "Submitting..."
                : isRegistering
                ? otpSent
                  ? "Verify OTP"
                  : "Send OTP"
                : "Login"}
            </button>
          </form>
        </div>
      </div>

      {showForgotPassword && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg w-full max-w-sm shadow-lg">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900">Reset Password</h2>
              <button
                onClick={() => {
                  setShowForgotPassword(false);
                  resetPasswordForm();
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            <form className="flex flex-col gap-3" onSubmit={handleForgotPasswordSubmit}>
              <TextInput
                type="email"
                name="email"
                placeholder="Email"
                value={resetData.email}
                onChange={handleResetChange}
                error={resetErrors.email}
                autoComplete="email"
              />
              {resetOtpSent && (
                <>
                  <TextInput
                    name="otp"
                    placeholder="Enter OTP"
                    value={resetData.otp}
                    onChange={handleResetChange}
                    error={resetErrors.otp}
                    autoComplete="off"
                  />
                  <TextInput
                    type="password"
                    name="newPassword"
                    placeholder="New Password"
                    value={resetData.newPassword}
                    onChange={handleResetChange}
                    error={resetErrors.newPassword}
                    autoComplete="new-password"
                  />
                  <TextInput
                    type="password"
                    name="confirmNewPassword"
                    placeholder="Confirm New Password"
                    value={resetData.confirmNewPassword}
                    onChange={handleResetChange}
                    error={resetErrors.confirmNewPassword}
                    autoComplete="new-password"
                  />
                </>
              )}
              <button
                type="submit"
                disabled={isSubmitting}
                className={`bg-gray-900 text-white font-semibold py-2 rounded-md transition-colors ${
                  isSubmitting ? "opacity-60 cursor-not-allowed" : "hover:bg-gray-800"
                }`}
              >
                {isSubmitting
                  ? "Submitting..."
                  : resetOtpSent
                  ? "Reset Password"
                  : "Send OTP"}
              </button>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default AuthForm;